import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import type { Dataset, Field, Row } from "@/warRoom/datasets/types";
import { fieldByKey } from "@/warRoom/datasets/registry";
import { lazyLabels, tr } from "@/warRoom/lib/i18n";

import { formByType } from "./chartForms";
import { labelOf } from "./format";
import {
  otherLabel,
  type Aggregation,
  type GraphSpec,
  type TimeGrain,
} from "./spec";

dayjs.extend(isoWeek);

export type Point = {
  /** 類別／時間軸的顯示值 */
  x: string;
  /** 聚合後的量值 */
  value: number;
  /** 數列名稱；沒有數列時是量值欄位名 */
  series: string;
  /** 落在這一格的原始筆數 */
  count: number;
};

export type ScatterPoint = {
  x: number;
  y: number;
  series: string;
  label: string;
};

export type AggregateResult = {
  points: Point[];
  scatter: ScatterPoint[];
  /** 數列名稱依序（決定配色 slot，順序穩定） */
  seriesNames: string[];
  /** X 軸類別依序 */
  categories: string[];
  /** 被折進「其他」的類別數 */
  foldedCategories: number;
  /** 被折進「其他」的數列數 */
  foldedSeries: number;
  /** 篩掉日期範圍後剩下的筆數 */
  rowCount: number;
  /** 原始筆數 */
  totalRows: number;
  xField?: Field;
  yField?: Field;
  seriesFieldMeta?: Field;
  valueLabel: string;
  /**
   * 數值該用什麼格式顯示。
   * 筆數與相異數是「個數」，不能沿用 Y 欄位的格式 —— 否則 3 個相異合作方會印成 NT$3。
   */
  valueFormat?: Field["format"];
  /**
   * 這些數字加起來有沒有意義。只有筆數和總和有；
   * 平均、極值、相異數都不能直接相加，所以合計／總計那一欄不能印。
   */
  additive: boolean;
  warnings: string[];
};

const GRAIN_FORMAT: Record<TimeGrain, string> = {
  day: "YYYY-MM-DD",
  week: "YYYY [W]WW",
  month: "YYYY-MM",
  quarter: "YYYY-[Q]Q",
  year: "YYYY",
};

export function timeBucket(raw: unknown, grain: TimeGrain): string | null {
  if (!raw) return null;
  const d = dayjs(String(raw));
  if (!d.isValid()) return null;
  if (grain === "week")
    return `${d.isoWeekYear()} W${String(d.isoWeek()).padStart(2, "0")}`;
  if (grain === "quarter")
    return `${d.year()}-Q${Math.floor(d.month() / 3) + 1}`;
  return d.format(GRAIN_FORMAT[grain]);
}

/** 一個 (X, 數列) 格子裡的原始素材：數值、筆數、相異值 */
type Cell = { values: number[]; count: number; distinct: Set<string> | null };

function applyAgg(agg: Aggregation, cell: Cell): number {
  if (agg === "count") return cell.count;
  if (agg === "countDistinct") return cell.distinct?.size ?? 0;
  const values = cell.values;
  if (values.length === 0) return 0;
  switch (agg) {
    case "sum":
      return values.reduce((a, b) => a + b, 0);
    case "avg":
      return values.reduce((a, b) => a + b, 0) / values.length;
    case "min":
      return Math.min(...values);
    case "max":
      return Math.max(...values);
  }
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** 依維度值篩選（同一欄位內是 OR，跨欄位是 AND） */
export function filterByDimensions(rows: Row[], spec: GraphSpec): Row[] {
  const active = Object.entries(spec.dimFilters).filter(
    ([, vals]) => vals.length > 0,
  );
  if (active.length === 0) return rows;
  return rows.filter((r) =>
    active.every(([key, vals]) => vals.includes(String(r[key] ?? ""))),
  );
}

/** 依日期範圍篩選（含端點） */
export function filterByDate(rows: Row[], spec: GraphSpec): Row[] {
  if (!spec.dateField || !spec.dateRange) return rows;
  const [from, to] = spec.dateRange;
  const start = dayjs(from).startOf("day");
  const end = dayjs(to).endOf("day");
  return rows.filter((r) => {
    const raw = r[spec.dateField!];
    if (!raw) return false;
    const d = dayjs(String(raw));
    return d.isValid() && !d.isBefore(start) && !d.isAfter(end);
  });
}

export function aggregate(
  dataset: Dataset,
  rows: Row[],
  spec: GraphSpec,
): AggregateResult {
  const warnings: string[] = [];
  const xField = spec.xField ? fieldByKey(dataset, spec.xField) : undefined;
  const yField = spec.yField ? fieldByKey(dataset, spec.yField) : undefined;
  const seriesFieldMeta = spec.seriesField
    ? fieldByKey(dataset, spec.seriesField)
    : undefined;

  const OTHER_LABEL = otherLabel();
  const countLabel = tr("warRoom.common.count");
  const filtered = filterByDate(filterByDimensions(rows, spec), spec);
  const valueLabel =
    spec.aggregation === "count"
      ? countLabel
      : tr("warRoom.aggregate.valueLabel", {
          agg: AGG_LABEL[spec.aggregation],
          field: yField?.label ?? countLabel,
        });
  const isCounting =
    spec.aggregation === "count" || spec.aggregation === "countDistinct";
  const valueFormat = isCounting ? ("int" as const) : yField?.format;
  const additive = spec.aggregation === "count" || spec.aggregation === "sum";

  const base: AggregateResult = {
    points: [],
    scatter: [],
    seriesNames: [],
    categories: [],
    foldedCategories: 0,
    foldedSeries: 0,
    rowCount: filtered.length,
    totalRows: rows.length,
    xField,
    yField,
    seriesFieldMeta,
    valueLabel,
    valueFormat,
    additive,
    warnings,
  };

  if (!spec.chartType || !spec.xField) return base;
  const form = formByType(spec.chartType);

  /* ------------------------------- 散佈圖 ------------------------------- */
  if (spec.chartType === "scatter") {
    if (!yField || !xField) return base;
    const seriesCounts = new Map<string, number>();
    const raw: ScatterPoint[] = [];
    for (const r of filtered) {
      const x = num(r[spec.xField]);
      const y = num(r[spec.yField!]);
      if (x === null || y === null) continue;
      const series = spec.seriesField
        ? labelOf(seriesFieldMeta, r[spec.seriesField])
        : yField.label;
      seriesCounts.set(series, (seriesCounts.get(series) ?? 0) + 1);
      raw.push({ x, y, series, label: String(r.name ?? r.id ?? "") });
    }
    const ranked = [...seriesCounts.entries()].sort((a, b) => b[1] - a[1]);
    const kept = ranked.slice(0, form.seriesCap).map(([n]) => n);
    const foldedSeries = Math.max(0, ranked.length - kept.length);
    if (foldedSeries > 0) {
      warnings.push(
        tr("warRoom.aggregate.warnings.scatterSeriesFolded", {
          cap: form.seriesCap,
          folded: foldedSeries,
          other: OTHER_LABEL,
        }),
      );
    }
    const keptSet = new Set(kept);
    return {
      ...base,
      scatter: raw.map((p) => ({
        ...p,
        series: keptSet.has(p.series) ? p.series : OTHER_LABEL,
      })),
      seriesNames: foldedSeries > 0 ? [...kept, OTHER_LABEL] : kept,
      foldedSeries,
    };
  }

  /* --------------------------- 類別／時間軸聚合 --------------------------- */
  const isTimeX = xField?.role === "time";
  const cells = new Map<string, Map<string, Cell>>(); // x -> series -> cell
  const seriesTotals = new Map<string, number>();
  const categoryTotals = new Map<string, number>();
  let skippedNoX = 0;

  for (const r of filtered) {
    const xRaw = r[spec.xField];
    const x = isTimeX
      ? timeBucket(xRaw, spec.timeGrain)
      : labelOf(xField, xRaw);
    if (x === null) {
      skippedNoX += 1;
      continue;
    }
    const series = spec.seriesField
      ? labelOf(seriesFieldMeta, r[spec.seriesField])
      : (yField?.label ?? countLabel);

    let bySeries = cells.get(x);
    if (!bySeries) {
      bySeries = new Map();
      cells.set(x, bySeries);
    }
    let cell = bySeries.get(series);
    if (!cell) {
      cell = { values: [], count: 0, distinct: null };
      bySeries.set(series, cell);
    }
    cell.count += 1;
    if (spec.yField) {
      const raw = r[spec.yField];
      if (spec.aggregation === "countDistinct") {
        // 相異數不看數值 —— 文字、日期、布林都算得出來，這就是非量值能當 Y 軸的原因
        if (raw !== null && raw !== undefined && raw !== "") {
          (cell.distinct ??= new Set()).add(String(raw));
        }
      } else {
        const v = num(raw);
        if (v !== null) cell.values.push(v);
      }
    }
  }

  if (skippedNoX > 0 && isTimeX) {
    warnings.push(
      tr("warRoom.aggregate.warnings.emptyTime", {
        count: skippedNoX,
        field: xField?.label,
      }),
    );
  }

  for (const [x, bySeries] of cells) {
    for (const [series, cell] of bySeries) {
      const v = applyAgg(spec.aggregation, cell);
      seriesTotals.set(series, (seriesTotals.get(series) ?? 0) + v);
      categoryTotals.set(x, (categoryTotals.get(x) ?? 0) + v);
    }
  }

  /* ----------------------------- 數列 top-N ----------------------------- */
  const rankedSeries = [...seriesTotals.entries()].sort((a, b) => b[1] - a[1]);
  const keptSeries = rankedSeries.slice(0, form.seriesCap).map(([n]) => n);
  const foldedSeries = Math.max(0, rankedSeries.length - keptSeries.length);
  if (foldedSeries > 0) {
    warnings.push(
      tr("warRoom.aggregate.warnings.seriesFolded", {
        cap: form.seriesCap,
        folded: foldedSeries,
        other: OTHER_LABEL,
      }),
    );
  }
  const keptSeriesSet = new Set(keptSeries);

  /* ---------------------------- 類別 top-N ---------------------------- */
  let categories = [...categoryTotals.keys()];
  let foldedCategories = 0;
  if (isTimeX) {
    categories.sort();
  } else if (spec.sortBy === "x") {
    categories.sort((a, b) => a.localeCompare(b, "zh-Hant"));
  } else {
    const dir = spec.sortBy === "valueAsc" ? 1 : -1;
    categories.sort(
      (a, b) =>
        dir * ((categoryTotals.get(b) ?? 0) - (categoryTotals.get(a) ?? 0)),
    );
  }
  // 時間軸不折疊（折了就斷了時間的連續性）
  if (!isTimeX && spec.topN > 0 && categories.length > spec.topN) {
    foldedCategories = categories.length - spec.topN;
    categories = categories.slice(0, spec.topN);
  }
  const keptCategorySet = new Set(categories);

  if (form.softCategoryCap && categories.length > form.softCategoryCap) {
    warnings.push(
      tr("warRoom.aggregate.warnings.tooManyCategories", {
        chart: form.label,
        cap: form.softCategoryCap,
        count: categories.length,
      }),
    );
  }

  /* ------------------------------ 產出點位 ------------------------------ */
  const merged = new Map<
    string,
    Map<string, { value: number; count: number }>
  >();
  const touch = (x: string, s: string) => {
    let bySeries = merged.get(x);
    if (!bySeries) {
      bySeries = new Map();
      merged.set(x, bySeries);
    }
    let cell = bySeries.get(s);
    if (!cell) {
      cell = { value: 0, count: 0 };
      bySeries.set(s, cell);
    }
    return cell;
  };

  const canMergeFolded = additive;
  for (const [x, bySeries] of cells) {
    const xKey = keptCategorySet.has(x) ? x : OTHER_LABEL;
    if (xKey === OTHER_LABEL && foldedCategories === 0) continue;
    for (const [series, cell] of bySeries) {
      const sKey = keptSeriesSet.has(series) ? series : OTHER_LABEL;
      const target = touch(xKey, sKey);
      target.value += applyAgg(spec.aggregation, cell);
      target.count += cell.count;
    }
  }
  if ((foldedCategories > 0 || foldedSeries > 0) && !canMergeFolded) {
    warnings.push(
      tr("warRoom.aggregate.warnings.nonAdditiveFold", {
        agg: AGG_LABEL[spec.aggregation],
        other: OTHER_LABEL,
        sum: AGG_LABEL.sum,
        count: AGG_LABEL.count,
      }),
    );
  }

  const finalCategories =
    foldedCategories > 0 ? [...categories, OTHER_LABEL] : categories;
  const finalSeries =
    foldedSeries > 0 ? [...keptSeries, OTHER_LABEL] : keptSeries;

  const points: Point[] = [];
  for (const x of finalCategories) {
    for (const s of finalSeries) {
      const cell = merged.get(x)?.get(s);
      points.push({
        x,
        series: s,
        value: cell
          ? spec.aggregation === "avg"
            ? round2(cell.value)
            : cell.value
          : 0,
        count: cell?.count ?? 0,
      });
    }
  }

  return {
    ...base,
    points,
    seriesNames: finalSeries,
    categories: finalCategories,
    foldedCategories,
    foldedSeries,
    warnings,
  };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export const AGG_LABEL = lazyLabels("warRoom.aggregation", [
  "count",
  "sum",
  "avg",
  "min",
  "max",
  "countDistinct",
]) as Record<Aggregation, string>;
